import React, { useEffect, useState } from "react";
import { Button, Skeleton } from "antd";
import {
  ReloadOutlined, SwapOutlined, FileTextOutlined,
  PhoneOutlined, WifiOutlined, CreditCardOutlined,
  BankOutlined, DollarOutlined, EyeOutlined, EyeInvisibleOutlined,
  RiseOutlined, BarChartOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDashboard } from "../../../store/context/DashboardContext";
import { useTranslation } from "react-i18next";
import { useFeatures } from "../../../store/context/feature-context";
import { featuresEnum } from "../../../shared/constants/constants";
import { useDashboardLayout } from "../../../store/context/dashboard-layout-context";
import SelectAccoutModal from "../../Payment Methods/Modals/SelectAccoutModal";
import DashboardCarousel from "../DashboardCarousel";
import DepositCard from "../../Policies/DepositCard";
import PolicyCard from "../../Policies/PolicyCard";
import { formatCurrencyWithoutCode, formatDate } from "../../../utils/helper/format";
import { hexToRgba } from "../../../utils/helper/colorUtils";
import { useTenant } from "../../../store/context/tenant-context";

const MinimalLayout = ({
  savedSessionData,
  notificationMenu,
  notificationList,
  onViewProducts,
  layoutSwitcher,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { findFeature } = useFeatures();
  const {
    selectedFilter,
    setSelectedFilter,
    policies,
    fetchPolicies,
    pendingQuotes,
    loading,
    depositPolicies,
    bankingProfile,
  } = useDashboard();

  const requiresAccount = bankingProfile?.hasCoreBankingAccounts === false;
  const { balanceVisible, toggleBalanceVisible } = useDashboardLayout();
  const tenant = useTenant();
  const primary = tenant?.theme?.colorPrimary || "#0b3d2e";
  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState(false);
  const [selectedAccountNumber, setSelectedAccountNumber] = useState(null);

  useEffect(() => {
    if (depositPolicies.length > 0 && !selectedAccountNumber) {
      setSelectedAccountNumber(depositPolicies[0].accountNumber);
    }
  }, [depositPolicies, selectedAccountNumber]);

  const selectedAccount =
    depositPolicies.find((a) => a.accountNumber === selectedAccountNumber) || depositPolicies[0];
  const selectedBalance =
    selectedAccount?.availableBalance || selectedAccount?.accountBalance || 0;

  const categories = [
    { key: "deposits", label: t("Deposits") },
    { key: "loans", label: t("Loans") },
    { key: "investments", label: t("Investments") },
  ].filter((c) => {
    if (c.key === "loans") return findFeature(featuresEnum.CUSTOMER_LOAN);
    return true;
  });

  const filteredPolicies = policies
    .slice()
    .sort((a, b) => a.type.localeCompare(b.type))
    .filter((p) => p.type.includes(selectedFilter));

  // Keep the account tab valid after policies load. Without this, Minimal can
  // retain a previously selected, empty tab after a layout change and make an
  // existing deposit account look as though it is missing.
  useEffect(() => {
    if (loading || selectedFilter !== "deposits" || policies.length === 0) return;

    const hasDeposits = policies.some((policy) => policy.type === "deposits");
    const hasLoans = policies.some((policy) => policy.type === "loans");
    if (!hasDeposits && hasLoans) setSelectedFilter("loans");
  }, [loading, policies, selectedFilter, setSelectedFilter]);

  const quickActions = [
    { Icon: SwapOutlined,    label: t("Transfer"),      path: "/transfer",        color: "#7c3aed", bg: "#f3eeff" },
    { Icon: PhoneOutlined,   label: t("Airtime"),       path: "/airtime",         color: "#1a5fac", bg: "#e8f0fc" },
    { Icon: WifiOutlined,    label: t("Data"),          path: "/data",            color: "#0b7a45", bg: "#e8f5ee" },
    { Icon: CreditCardOutlined, label: t("Bills"),      path: "/bills-payment",   color: "#e5791b", bg: "#fff4ea" },
    { Icon: FileTextOutlined, label: t("Statement"),    path: "/account-statement", color: "#555",  bg: "#f5f5f5" },
    { Icon: DollarOutlined,  label: t("Save"),          path: "/my-savings",      color: "#b45309", bg: "#fef9e7", disabled: requiresAccount },
  ];

  const stats = [
    { label: t("Active Loans"),    value: policies.filter((p) => p.type === "loans").length,        Icon: FileTextOutlined, color: "#1a5fac", bg: "#e8f0fc" },
    { label: t("Savings Rate"),    value: "10% p.a.",                                                Icon: RiseOutlined,     color: "#0b7a45", bg: "#e8f5ee" },
    { label: t("Investments"),     value: policies.filter((p) => p.type === "investments").length,  Icon: BarChartOutlined, color: "#7c3aed", bg: "#f3eeff" },
  ];

  return (
    <div style={{ background: "#f6f6f8", minHeight: "100vh", padding: "24px 28px" }}>
      <SelectAccoutModal
        isModalVisible={isSwitchModalOpen}
        handleCancel={() => setIsSwitchModalOpen(false)}
        handleOk={(accountNumber) => { setSelectedAccountNumber(accountNumber); setIsSwitchModalOpen(false); }}
      />

      {/* â”€â”€ TOP ROW: balance card + stats â”€â”€ */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 16, marginBottom: 16, alignItems: "stretch" }}>

        {/* Balance card â€” white, type-forward, no gradient */}
        <div style={{ background: "#fff", borderRadius: 18, padding: "28px 32px", border: "1px solid #efefef", boxShadow: "0 1px 8px rgba(0,0,0,0.04)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "1.5px", color: "#bbb", margin: 0 }}>
              {t("Account Balance")}
            </p>
            <button
              onClick={toggleBalanceVisible}
              style={{ background: "none", border: `1px solid #e5e5e5`, borderRadius: 6, padding: "2px 9px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, color: "#888", fontSize: 11 }}
            >
              {balanceVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              {balanceVisible ? t("Hide") : t("Show")}
            </button>
          </div>

          <p style={{ fontSize: 42, fontWeight: 800, color: "#0f0f0f", margin: "0 0 12px", letterSpacing: "-2px", lineHeight: 1 }}>
            ₦{balanceVisible ? formatCurrencyWithoutCode(selectedBalance) : "***.**"}
          </p>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "#666", fontWeight: 500 }}>
              {selectedAccount?.accountType || t("Savings")}
            </span>
            {selectedAccount?.accountNumber && (
              <span style={{ fontSize: 12, color: "#444", background: "#f5f5f5", padding: "3px 10px", borderRadius: 20, fontFamily: "monospace", letterSpacing: "1px", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid #e5e5e5" }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: selectedAccount.active ? "#22c55e" : "#f87171", flexShrink: 0 }} />
                {selectedAccount.accountNumber}
              </span>
            )}
            {depositPolicies.length > 1 && (
              <button onClick={() => setIsSwitchModalOpen(true)} style={{ fontSize: 12, color: primary, background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: 0, marginLeft: 4 }}>
                {t("Switch")} ›
              </button>
            )}
          </div>
        </div>

        {/* Stats column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {stats.map((s) => (
            <div key={s.label} style={{ background: "#fff", borderRadius: 14, padding: "14px 20px", border: "1px solid #efefef", display: "flex", alignItems: "center", gap: 14, minWidth: 200, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: s.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <s.Icon style={{ fontSize: 17, color: s.color }} />
              </div>
              <div>
                <p style={{ fontSize: 11, color: "#bbb", textTransform: "uppercase", letterSpacing: ".5px", margin: 0 }}>{s.label}</p>
                <p style={{ fontSize: 18, fontWeight: 700, color: "#111", margin: 0 }}>{s.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* â”€â”€ QUICK ACTIONS â”€â”€ */}
      <div style={{ background: "#fff", borderRadius: 16, padding: "18px 22px", marginBottom: 16, border: "1px solid #efefef", boxShadow: "0 1px 6px rgba(0,0,0,0.03)" }}>
        <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "1px", color: "#bbb", margin: "0 0 14px", fontWeight: 600 }}>{t("Quick Actions")}</p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {quickActions.map(({ Icon, label, path, color, bg, disabled }) => (
            <button
              key={label}
              disabled={disabled}
              onClick={() => navigate(path)}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 18px", borderRadius: 12, border: "1.5px solid #efefef",
                background: "#fafafa", cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.4 : 1, transition: "all .15s",
              }}
              onMouseOver={(e) => { if (!disabled) { e.currentTarget.style.borderColor = hexToRgba(primary, 0.4); e.currentTarget.style.background = hexToRgba(primary, 0.04); } }}
              onMouseOut={(e) => { e.currentTarget.style.borderColor = "#efefef"; e.currentTarget.style.background = "#fafafa"; }}
            >
              <div style={{ width: 32, height: 32, borderRadius: 9, background: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon style={{ fontSize: 15, color }} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#333", whiteSpace: "nowrap" }}>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* â”€â”€ MAIN CONTENT GRID â”€â”€ */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 16 }}>

        {/* Left: Accounts */}
        <div style={{ background: "#fff", borderRadius: 16, padding: "20px 22px", border: "1px solid #efefef", boxShadow: "0 1px 6px rgba(0,0,0,0.03)", minWidth: 0, overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <p style={{ fontWeight: 700, fontSize: 14, color: "#0f0f0f", margin: 0 }}>{t("My Accounts")}</p>
            <Button icon={<ReloadOutlined />} size="small" type="text" onClick={fetchPolicies} />
          </div>

          {/* Tab strip */}
          <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: "1px solid #f0f0f0", paddingBottom: 0 }}>
            {categories.map((c) => (
              <button
                key={c.key}
                disabled={requiresAccount && c.key !== "loans"}
                onClick={() => setSelectedFilter(c.key)}
                style={{
                  padding: "7px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                  border: "none", background: "none", transition: "all .15s",
                  color: selectedFilter === c.key ? primary : "#aaa",
                  borderBottom: selectedFilter === c.key ? `2px solid ${primary}` : "2px solid transparent",
                  marginBottom: -1,
                }}
              >
                {c.label}
              </button>
            ))}
          </div>

          {loading ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : filteredPolicies.length > 0 ? (
            <div style={{ overflow: "hidden" }}>
              <DashboardCarousel
                slides={filteredPolicies.map((policy) =>
                  policy.type === "deposits" || policy.type === "loans" ? (
                    <DepositCard key={policy.policyNumber} policy={policy} balanceVisible={balanceVisible} />
                  ) : (
                    <PolicyCard key={policy.policyNumber} policy={policy} />
                  )
                )}
              />
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <BankOutlined style={{ fontSize: 32, color: "#ddd", display: "block", marginBottom: 8 }} />
              <p style={{ color: "#bbb", fontSize: 13, margin: "0 0 12px" }}>{`${t("No")} ${selectedFilter} ${t("available")}`}</p>
              <Button size="small" type="primary" disabled={requiresAccount} onClick={onViewProducts}>{t("Get Started")}</Button>
            </div>
          )}
        </div>

        {/* Right: Pending Applications */}
        <div style={{ background: "#fff", borderRadius: 16, padding: "20px 22px", border: "1px solid #efefef", boxShadow: "0 1px 6px rgba(0,0,0,0.03)", display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <p style={{ fontWeight: 700, fontSize: 14, color: "#0f0f0f", margin: 0 }}>{t("My Pending Applications")}</p>
            <button onClick={() => navigate("/loans")} style={{ fontSize: 12, color: primary, background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: 0 }}>
              {t("View all")} →
            </button>
          </div>

          {!pendingQuotes?.length ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 0", color: "#ccc" }}>
              <FileTextOutlined style={{ fontSize: 32, marginBottom: 8 }} />
              <p style={{ fontSize: 12, color: "#ccc", margin: 0 }}>{t("No pending applications")}</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {pendingQuotes.slice(0, 5).map((quote, i) => (
                <div
                  key={i}
                  onClick={() => navigate(`/loans/${quote.id}`)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 8px", borderRadius: 10, cursor: "pointer", transition: "background .1s" }}
                  onMouseOver={(e) => { e.currentTarget.style.background = "#f9f9f9"; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: hexToRgba(primary, 0.08), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <FileTextOutlined style={{ color: primary, fontSize: 15 }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontWeight: 600, fontSize: 13, color: "#111", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {quote.accountType || t("Loan")}
                    </p>
                    <p style={{ fontSize: 11, color: "#bbb", margin: 0 }}>{formatDate(quote.dateOpened)}</p>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: "#111", margin: 0 }}>
                      ₦{parseFloat(quote.loanAmount || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                    </p>
                    <span style={{ fontSize: 10, padding: "1px 8px", background: hexToRgba(primary, 0.1), color: primary, borderRadius: 20, fontWeight: 600, display: "inline-block", marginTop: 2 }}>
                      Pending
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MinimalLayout;
